import { catalog } from '../core/catalog.js';
import { t } from '../core/i18n.js';
import { device } from '../core/device.js';
import { settings } from '../core/storage.js';
import { escapeHtml, formatDuration, formatMinutes, formatClock, cleanName } from '../core/utils.js';
import { epg, programProgress } from '../core/epg.js';
import { PlayerCore } from '../core/player.js';
import { previewChannel } from '../core/preview-variant.js';
import { nativePreviewCore, physicalRect } from '../core/native-player.js';
import { parseAge } from '../core/age.js';
import { splitProgramDesc } from '../core/epg-desc.js';
import { imageUrl, ratingPill, titleOf, programMeta } from './render.js';
import { genreList } from '../core/genres.js';
import { previewMuted, setPreviewMuted, onPreviewMute } from '../core/preview-sound.js';
import { previewEnabled, trailerEmbedUrl, trailerEventsWork, embeddableTrailerId } from './preview.js';

/* Tarjeta ampliada (como Prime/Netflix): al dejar el ratón sobre una carátula, o el foco del mando en la tele, crece
   por encima de la fila y enseña la imagen apaisada, el título, los datos, los botones y la sinopsis, sin abrir la ficha.
   En móvil no existe (no hay «pararse encima»). En la tele es solo informativa: OK sigue abriendo la ficha y OK
   mantenido las opciones; no se puede bajar a sus botones (no se pintan). */

const HOVER_MS = 450;   // ratón quieto sobre la tarjeta
const FOCUS_MS = 800;   // foco del mando (pasar deprisa por la fila no abre nada)
const LEAVE_MS = 160;   // margen al salir hacia la propia tarjeta ampliada
const WARM_MS = 150;    // tras este rato sobre una carátula ya se va pidiendo su ficha (y su imagen apaisada)
const HIDE_ANIM_MS = 180;
const ART_FADE_MS = 320;
const LIVE_MS = 600;           // un canal en directo tarda en arrancar: se pide antes que un tráiler
const TRAILER_MS = 1400;       // quieto este rato con la tarjeta abierta y, si hay tráiler, sustituye a la imagen
const TRAILER_GIVE_UP_MS = 9000; // el directo tarda más en arrancar (+8 s) // si no ha empezado a sonar, se queda la imagen
const TRAILER_MAX_MS = 45000;    // y no se queda sonando eternamente

let box = null;      // la tarjeta ampliada en pantalla (o null)
let anchor = null;   // la carátula a la que pertenece
let showTimer = null;
let hideTimer = null;
let token = 0;       // invalida pedidos de ficha en curso al cambiar de tarjeta
let warmTimer = null;
let trailer = null;  // tráiler que suena en la tarjeta: { frame, button, onMessage, timers }
let trailerTimer = null;
const inflight = new Map(); // «kind:id» → promesa de la ficha en curso (para no pedirla dos veces)

/* La primera vez que se abre una tarjeta la ficha viene del proveedor y de TMDB (uno o dos segundos). Para que no se
   note: la ficha se pide en cuanto el ratón o el foco llevan un instante sobre la carátula (antes de que la tarjeta
   salga), y la tarjeta sale de inmediato con lo que ya hay (carátula, título, año, nota) y se completa sola
   (sinopsis, datos y la imagen apaisada, que entra con un fundido cuando ha cargado). */
function fetchInfo(kind, id) {
    const have = catalog.peekInfo(kind, id);
    if (have) return Promise.resolve(have);
    const key = kind + ':' + id;
    if (inflight.has(key)) return inflight.get(key);
    const p = (kind === 'vod' ? catalog.vodInfo(id) : catalog.seriesInfo(id)).catch(() => null).then((info) => {
        inflight.delete(key);
        if (info && info.backdrop) { const im = new Image(); im.src = imageUrl(info.backdrop); } // que vaya cargando
        return info;
    });
    inflight.set(key, p);
    return p;
}

function kindId(card) {
    const live = card.getAttribute('data-play-live');
    if (live) return ['live', live];
    const [kind, id] = String(card.getAttribute('data-open') || '').split(':');
    return kind === 'vod' || kind === 'series' ? [kind, id] : [null, null];
}

function warm(card) {
    clearTimeout(warmTimer);
    warmTimer = setTimeout(() => {
        const [kind, id] = kindId(card);
        if (kind && kind !== 'live' && catalog.get(kind, id) && document.contains(card)) fetchInfo(kind, id);
    }, WARM_MS);
}

function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function cardOf(target) {
    const card = target && target.closest ? target.closest('.card[data-open], .card.card-channel[data-play-live], .chitem[data-play-live]') : null;
    return card && card.classList.contains('has-expand') ? null : card; // las que crecen dentro de la fila (peekStyle «row») no abren la flotante
}

function clearTimers() {
    clearTimeout(showTimer);
    clearTimeout(hideTimer);
    clearTimeout(warmTimer);
    showTimer = null;
    hideTimer = null;
    warmTimer = null;
}

function schedule(card, ms) {
    if (card === anchor) { clearTimeout(hideTimer); hideTimer = null; return; }
    // Al pasar a otra carátula, la tarjeta abierta se cierra al momento; la nueva espera su tiempo.
    if (box) hidePeek();
    clearTimers();
    warm(card);
    showTimer = setTimeout(() => show(card), ms);
}

/* Tráiler en la tarjeta: tras un momento sobre ella, el tráiler (mudo) sustituye a la imagen apaisada con un fundido; un botón
   activa el sonido. Si no hay tráiler, no se puede incrustar o el ajuste de vistas previas está apagado, la tarjeta no cambia. */
function stopTrailer() {
    clearTimeout(trailerTimer);
    trailerTimer = null;
    if (!trailer) return;
    const tr = trailer;
    trailer = null;
    tr.timers.forEach(clearTimeout);
    if (tr.sync) clearInterval(tr.sync);
    if (tr.offMute) tr.offMute();
    window.removeEventListener('message', tr.onMessage);
    tr.frame.classList.remove('is-playing');
    if (tr.core) { try { tr.core.destroy(); } catch (e) { /* ya suelto */ } }
    if (tr.button.parentNode) tr.button.parentNode.removeChild(tr.button);
    if (tr.wait && tr.wait.parentNode) tr.wait.parentNode.removeChild(tr.wait);
    setTimeout(() => { if (!tr.core) tr.frame.src = 'about:blank'; if (tr.frame.parentNode) tr.frame.parentNode.removeChild(tr.frame); }, reducedMotion() ? 0 : ART_FADE_MS);
}

function scheduleTrailer(info, kind) {
    clearTimeout(trailerTimer);
    trailerTimer = null;
    if (trailer || !previewEnabled()) return;
    if (kind === 'live') { // también en la tele: al enfocar un canal suena (mudo) hasta que se pulsa OK

        const my = token;
        trailerTimer = setTimeout(() => { trailerTimer = null; if (box && token === my && !trailer) startLive(info); }, LIVE_MS);
        return;
    }
    if (device.isTV || !trailerEventsWork()) return;
    const id = embeddableTrailerId(info);
    if (!id) return;
    const my = token;
    trailerTimer = setTimeout(() => { trailerTimer = null; if (box && token === my && !trailer) startTrailer(id); }, TRAILER_MS);
}

/** Canal en directo: la emisión suena muda en lugar del logo. Gasta una conexión del proveedor mientras la tarjeta está abierta. */
function startLive(ch) {
    const art = box && box.querySelector('.peek-art');
    if (!art) return;
    let url = '';
    try { url = catalog.liveUrl(previewChannel(ch)); } catch (e) { url = ''; } // la versión más ligera del canal: arranca antes
    if (!url) return;
    // En la app de Android lo pinta el reproductor nativo (el de pantalla completa) por encima de la tarjeta, solo en su rectángulo.
    const ncore = nativePreviewCore();
    const video = document.createElement(ncore ? 'div' : 'video');
    video.className = 'peek-trailer';
    if (!ncore) {
        video.muted = previewMuted();
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.poster = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'peek-sound';
    button.hidden = true;
    button.setAttribute('aria-label', t('preview.sound'));
    const core = ncore || new PlayerCore(video);
    const vmuted = () => !!core.video.muted;
    const hint = device.isTV ? '<span class="peek-sound-hint">' + escapeHtml(t('peek.soundHint')) + '</span>' : '';
    const icon = () => { button.innerHTML = '<span class="mi">' + (vmuted() ? 'volume_off' : 'volume_up') + '</span>' + hint; };
    icon();
    button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        setPreviewMuted(!vmuted()); // estado general: lo siguen las demás vistas previas
    });
    const wait = document.createElement('div'); // mientras conecta, un indicador discreto
    wait.className = 'spinner peek-wait';
    const tr = { frame: video, button, timers: [], onMessage: () => {}, core, wait };
    tr.offMute = onPreviewMute((m) => { core.video.muted = m; icon(); });
    core.on('autoplayblocked', () => { core.video.muted = true; icon(); if (!ncore) video.play().catch(() => {}); }); // sin permiso para sonar: en silencio
    trailer = tr;
    art.appendChild(video);
    art.appendChild(wait);
    art.appendChild(button);
    core.on('playing', () => {
        if (trailer !== tr) return;
        tr.timers.forEach(clearTimeout);
        tr.timers = [setTimeout(stopTrailer, TRAILER_MAX_MS)];
        video.classList.add('is-playing');
        button.hidden = false;
        if (wait.parentNode) wait.parentNode.removeChild(wait);
    });
    core.on('error', () => { if (trailer === tr) stopTrailer(); });
    core.on('ended', () => { if (trailer === tr) stopTrailer(); });
    tr.timers.push(setTimeout(stopTrailer, TRAILER_GIVE_UP_MS + 8000));
    // La ventanita nativa se pinta aparte de la página: se vuelve a medir la carátula mientras suena (la tarjeta se anima, cambia de
    // alto al llegar el texto o se recoloca) para que no se desplace ni tape el título.
    if (ncore) {
        tr.sync = setInterval(() => {
            if (trailer !== tr || !box) return;
            const a = box.querySelector('.peek-art');
            if (!a) return;
            const r = physicalRect(a);
            const key = r.join(',');
            if (key !== tr.lastRect) { tr.lastRect = key; core.setRect(r); }
        }, 250);
    }
    core.load({ url, live: true, muted: previewMuted(), autoplay: true, rect: ncore ? physicalRect(art) : null, layer: 'front', cssClass: '' }).catch(() => { if (trailer === tr) stopTrailer(); });
}

function startTrailer(id) {
    const art = box.querySelector('.peek-art');
    if (!art) return;
    const frame = document.createElement('iframe');
    frame.className = 'peek-trailer';
    frame.setAttribute('allow', 'autoplay; encrypted-media');
    frame.setAttribute('tabindex', '-1');
    frame.setAttribute('aria-hidden', 'true');
    frame.title = 'Trailer';
    let muted = previewMuted();
    frame.src = trailerEmbedUrl(id, 0, muted);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'peek-sound';
    button.hidden = true;
    button.setAttribute('aria-label', t('preview.sound'));
    const hint = device.isTV ? '<span class="peek-sound-hint">' + escapeHtml(t('peek.soundHint')) + '</span>' : '';
    const icon = () => { button.innerHTML = '<span class="mi">' + (muted ? 'volume_off' : 'volume_up') + '</span>' + hint; };
    icon();
    const command = (func, args = []) => { try { frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args }), '*'); } catch (e) { /* aún no cargado */ } };
    button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        setPreviewMuted(!muted); // estado general: lo siguen las demás vistas previas
    });
    const tr = { frame, button, timers: [], onMessage: null };
    tr.offMute = onPreviewMute((m) => {
        muted = m;
        command(muted ? 'mute' : 'unMute');
        if (!muted) command('setVolume', [100]);
        icon();
    });
    tr.onMessage = (e) => {
        if (e.source !== frame.contentWindow) return;
        let data = e.data;
        if (typeof data === 'string') { try { data = JSON.parse(data); } catch (err) { return; } }
        if (!data || typeof data !== 'object') return;
        if (data.event === 'onError') { stopTrailer(); return; }
        const info = data.info;
        const state = data.event === 'onStateChange' ? info : info && typeof info === 'object' ? info.playerState : undefined;
        if (state === 1 && !frame.classList.contains('is-playing')) {
            tr.timers.forEach(clearTimeout);
            tr.timers = [setTimeout(stopTrailer, TRAILER_MAX_MS)];
            frame.classList.add('is-playing');
            button.hidden = false;
            command('unloadModule', ['captions']);
        } else if (state === 0) {
            stopTrailer(); // acabó: vuelve la imagen
        }
    };
    window.addEventListener('message', tr.onMessage);
    frame.addEventListener('load', () => {
        try { frame.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), '*'); } catch (err) { /* nada */ }
        command('addEventListener', ['onStateChange']);
        command('unloadModule', ['captions']);
    });
    tr.timers.push(setTimeout(stopTrailer, TRAILER_GIVE_UP_MS));
    trailer = tr;
    art.appendChild(frame);
    art.appendChild(button);
}

/** Cierra la tarjeta ampliada (con su fundido) y anula lo pendiente. */
export function hidePeek() {
    clearTimers();
    stopTrailer();
    token++;
    anchor = null;
    const el = box;
    box = null;
    if (!el) return;
    el.classList.remove('is-in');
    setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, reducedMotion() ? 0 : HIDE_ANIM_MS);
}

function scheduleHide() {
    clearTimeout(showTimer);
    showTimer = null;
    if (!box || hideTimer) return;
    hideTimer = setTimeout(hidePeek, LEAVE_MS);
}

const playerOpen = () => document.documentElement.classList.contains('player-open');

function show(card) {
    showTimer = null;
    if (playerOpen()) return;
    if (!document.contains(card)) return;
    const [kind, id] = kindId(card);
    if (!kind) return;
    const base = catalog.get(kind, id);
    if (!base) return;
    const my = ++token;
    anchor = card;
    if (kind === 'live') { render(base, base, kind, id, card); return; }
    const have = catalog.peekInfo(kind, id);
    render(have || base, base, kind, id, card);
    if (have) return;
    fetchInfo(kind, id).then((full) => { if (full && token === my && anchor === card && box) update(full, base, kind, id, card); });
}

/** Completa la tarjeta ya abierta con la ficha recién llegada: textos en el sitio y la imagen apaisada con fundido. */
function update(info, base, kind, id, card) {
    const body = box.querySelector('.peek-body');
    if (body) body.innerHTML = bodyHtml(info, base, kind, id);
    const art = box.querySelector('.peek-art');
    if (art && info.backdrop && !art.querySelector('.peek-img.is-wide')) {
        const im = new Image();
        im.className = 'peek-img is-wide is-fading';
        im.alt = '';
        im.onload = () => {
            if (!box || !box.contains(art)) return;
            const old = Array.from(art.querySelectorAll('.peek-img, .peek-noart'));
            art.appendChild(im);
            requestAnimationFrame(() => im.classList.remove('is-fading'));
            setTimeout(() => old.forEach((n) => { if (n.parentNode) n.parentNode.removeChild(n); }), reducedMotion() ? 0 : ART_FADE_MS);
        };
        im.src = imageUrl(info.backdrop);
    }
    place(card, { keepTop: true });
    scheduleTrailer(info, 'vod');
}

function stateLine(kind, id, info, blocked) {
    if (blocked) return `<span class="mi">lock</span>${escapeHtml(parseAge(info.age).label || '')}`;
    if (kind === 'vod') {
        const p = catalog.getProgress('vod', id);
        if (p && p.finished) return `<span class="mi">check</span>${escapeHtml(t('watched.badge'))}`;
        if (p && p.pct > 0) return `<span class="mi">play_circle</span>${escapeHtml(t('common.resume'))} · ${escapeHtml(formatDuration(p.position))}`;
        return `<span class="mi">movie</span>${escapeHtml(t('detail.kindMovie'))}`;
    }
    const p = catalog.seriesProgress(id);
    if (p && !p.finished) return `<span class="mi">play_circle</span>${escapeHtml(t('series.continue', { season: p.season, episode: p.episode }))}`;
    return `<span class="mi">tv</span>${escapeHtml(t('detail.kindSeries'))}`;
}

function playAttrs(kind, id, info) {
    if (kind === 'vod') {
        const p = catalog.getProgress('vod', id);
        return `data-play="vod:${escapeHtml(id)}" data-resume-pos="${p && !p.finished && p.pct > 0 ? p.position : 0}"`;
    }
    const p = catalog.seriesProgress(id);
    if (p && !p.finished) return `data-play-episode="${escapeHtml(id)}:${p.season}:${p.episode}" data-resume-pos="${p.position || 0}"`;
    const first = (info.seasons || []).map((s) => s.episodes && s.episodes[0]).find(Boolean);
    if (first) return `data-play-episode="${escapeHtml(id)}:${first.season}:${first.num}" data-resume-pos="0"`;
    return 'data-peek-open'; // sin episodios a mano: a la ficha
}

function actionsHtml(kind, id, info, blocked) {
    if (device.isTV) return '';
    if (kind === 'live') {
        const fav = catalog.isFavorite('live', id);
        return `<div class="peek-actions">
            <button type="button" class="btn btn-icon btn-round peek-btn peek-play" data-play-live="${escapeHtml(id)}" aria-label="${escapeHtml(t('common.play'))}" title="${escapeHtml(t('common.play'))}"><span class="mi">play_arrow</span></button>
            <button type="button" class="btn btn-icon btn-round peek-btn ${fav ? 'is-active' : ''}" data-fav="live:${escapeHtml(id)}" aria-label="${escapeHtml(fav ? t('common.favoriteRemove') : t('common.favoriteAdd'))}" title="${escapeHtml(fav ? t('common.favoriteRemove') : t('common.favoriteAdd'))}"><span class="mi">${fav ? 'check' : 'add'}</span></button>
        </div>`;
    }
    const fav = catalog.isFavorite(kind, id);
    return `<div class="peek-actions">
        ${blocked ? '' : `<button type="button" class="btn btn-icon btn-round peek-btn peek-play" ${playAttrs(kind, id, info)} aria-label="${escapeHtml(t('common.play'))}" title="${escapeHtml(t('common.play'))}"><span class="mi">play_arrow</span></button>`}
        <button type="button" class="btn btn-icon btn-round peek-btn ${fav ? 'is-active' : ''}" data-fav="${kind}:${escapeHtml(id)}" aria-label="${escapeHtml(fav ? t('common.favoriteRemove') : t('common.favoriteAdd'))}" title="${escapeHtml(fav ? t('common.favoriteRemove') : t('common.favoriteAdd'))}"><span class="mi">${fav ? 'check' : 'add'}</span></button>
        <button type="button" class="btn btn-icon btn-round peek-btn" data-peek-open aria-label="${escapeHtml(t('detail.moreInfo'))}" title="${escapeHtml(t('detail.moreInfo'))}"><span class="mi">info</span></button>
    </div>`;
}

function metaHtml(kind, info, blocked) {
    const meta = [];
    if (info.year) meta.push(`<span>${escapeHtml(info.year)}</span>`);
    if (info.rating) meta.push(ratingPill(info.rating));
    if (kind === 'vod' && info.durationSecs) meta.push(`<span>${escapeHtml(formatMinutes(info.durationSecs))}</span>`);
    if (kind === 'series' && Number(info.episodeRunTime) > 0) meta.push(`<span>${escapeHtml(info.episodeRunTime)} ${escapeHtml(t('common.minutes'))}</span>`);
    const age = parseAge(info.age);
    if (age.label) meta.push(`<span class="tag tag-age ${blocked ? 'is-blocked' : ''}">${escapeHtml(age.label)}</span>`);
    const genres = genreList(info.genre);
    if (genres.length) meta.push(`<span>${escapeHtml(genres[0])}</span>`);
    return meta.length ? `<div class="peek-meta">${meta.join('')}</div>` : '';
}

function artHtml(info, base) {
    const wide = info.backdrop || '';
    const poster = info.poster || base.poster || '';
    if (wide) return `<img class="peek-img is-wide" src="${escapeHtml(imageUrl(wide))}" alt=""/>`;
    if (poster) return `<img class="peek-img is-blur" src="${escapeHtml(imageUrl(poster))}" alt=""/><img class="peek-img is-poster" src="${escapeHtml(imageUrl(poster))}" alt=""/>`;
    return `<span class="peek-noart"><span class="mi">${info.seasons ? 'tv' : 'movie'}</span></span>`;
}

/** La imagen de un canal es su logo: se pone centrado sobre un fondo borroso de sí mismo (si no tiene, un icono). */
function liveArtHtml(ch) {
    if (!ch.logo) return '<span class="peek-noart"><span class="mi">live_tv</span></span>';
    const src = escapeHtml(imageUrl(ch.logo));
    return `<img class="peek-img is-blur" src="${src}" alt=""/><img class="peek-img is-poster peek-logo" src="${src}" alt=""/>`;
}

function liveBodyHtml(ch, id) {
    const nn = epg.nowNext(ch.epgId);
    const name = cleanName(ch.name);
    const now = nn.now;
    const prog = now ? splitProgramDesc(now.desc) : null;
    return `<div class="peek-title">${escapeHtml(now ? now.title : name)}</div>
        <div class="peek-state"><span class="mi">sensors</span>${escapeHtml(t('common.live'))} · ${escapeHtml(name)}</div>
        ${actionsHtml('live', id, ch, false)}
        ${now ? `<div class="peek-meta"><span>${escapeHtml(formatClock(now.start))} - ${escapeHtml(formatClock(now.stop))}</span></div><div class="card-epg-bar peek-epg"><i style="width:${programProgress(now)}%"></i></div>` : ''}
        ${prog ? programMeta(prog) : ''}
        ${prog && prog.plot ? `<p class="peek-plot">${escapeHtml(prog.plot)}</p>` : nn.next && !(prog && prog.hasMeta) ? `<p class="peek-plot">${escapeHtml(formatClock(nn.next.start))} · ${escapeHtml(nn.next.title)}</p>` : ''}`;
}

function bodyHtml(info, base, kind, id) {
    if (kind === 'live') return liveBodyHtml(base, id);
    const blocked = catalog.ageBlocked(info);
    return `<div class="peek-title">${escapeHtml(titleOf(info) || titleOf(base))}</div>
        <div class="peek-state">${stateLine(kind, id, info, blocked)}</div>
        ${actionsHtml(kind, id, info, blocked)}
        ${metaHtml(kind, info, blocked)}
        ${info.plot ? `<p class="peek-plot">${escapeHtml(info.plot)}</p>` : ''}`;
}

function render(info, base, kind, id, card) {
    const html = `
        <div class="peek-art">${kind === 'live' ? liveArtHtml(info) : artHtml(info, base)}</div>
        <div class="peek-body">${bodyHtml(info, base, kind, id)}</div>`;
    const fresh = !box;
    if (fresh) {
        box = document.createElement('div');
        box.className = 'peek' + (device.isTV ? ' is-tv' : '');
        box.setAttribute('role', 'presentation');
        box.addEventListener('click', onBoxClick);
        box.addEventListener('wheel', hidePeek, { passive: true });
        document.getElementById('app').appendChild(box);
    }
    stopTrailer();
    box.innerHTML = html;
    // Pinchar en la ventana abre lo mismo que su tarjeta: la ficha (película/serie) o el canal en directo. Un canal NO lleva
    // `data-open`: el manejador general lo tomaría por una ficha y abriría /series/<id del canal>, que no existe.
    delete box.dataset.open;
    delete box.dataset.playLive;
    if (kind === 'live') box.dataset.playLive = id; else box.dataset.open = kind + ':' + id;
    place(card);
    scheduleTrailer(info, kind);
    if (fresh) {
        if (reducedMotion()) box.classList.add('is-in');
        else requestAnimationFrame(() => requestAnimationFrame(() => { if (box) box.classList.add('is-in'); }));
    }
}

let probe = null; // elemento invisible que ocupa toda la ventana: dice cómo mide el navegador la pantalla y la escala (zoom del modo TV)

/** Medidas de la pantalla en las mismas unidades que getBoundingClientRect(), y cuántas de esas unidades son un píxel CSS (`k`). */
function screenMetrics() {
    if (!probe || !probe.isConnected) {
        probe = document.createElement('div');
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:fixed;left:0;top:0;right:0;bottom:0;visibility:hidden;pointer-events:none;transform:none;';
        document.getElementById('app').appendChild(probe);
    }
    const r = probe.getBoundingClientRect();
    const k = probe.offsetWidth > 0 ? r.width / probe.offsetWidth : 1;
    return { vw: r.width, vh: r.height, k: k > 0 && isFinite(k) ? k : 1 };
}

/**
 * Centrada sobre la carátula o el canal enfocados, sin salirse de la pantalla. En la tele la interfaz se reduce con `zoom`: las
 * medidas del navegador y los píxeles CSS ya no coinciden, así que se calcula en las unidades del navegador y se pasa a CSS al final.
 */
function place(card, { keepTop = false } = {}) {
    if (!box || !card) return;
    const r = card.getBoundingClientRect();
    const { vw, vh, k } = screenMetrics();
    const wide = card.classList.contains('card-wide');
    const row = card.classList.contains('chitem'); // fila de la lista de canales: ocupa todo el ancho, la ventana sale a la derecha
    const wMax = Math.min(440 * k, vw - 16 * k);
    const w = Math.round(Math.min(Math.max(wide ? r.width * 1.35 : r.width * 1.95, 300 * k), wMax));
    box.style.width = (w / k) + 'px';
    const h = box.offsetHeight * k;
    const edge = 8 * k;
    let left = Math.round(Math.min(Math.max(r.left + r.width / 2 - w / 2, edge), vw - edge - w));
    if (row) left = Math.round(vw - edge - w - (device.isTV ? 24 : 64) * k);
    // Centrada sobre la carátula o el canal enfocado (también en la tele): lo tapa y ocupa su sitio.
    const wanted = keepTop && box.style.top ? parseFloat(box.style.top) * k : r.top + r.height / 2 - h / 2;
    const top = Math.round(Math.min(Math.max(wanted, edge), Math.max(edge, vh - edge - h)));
    box.style.left = (left / k) + 'px';
    box.style.top = (top / k) + 'px';
    // La ventanita nativa (app de Android) sigue a su tarjeta.
    if (trailer && trailer.core && trailer.core.setRect) { const art = box.querySelector('.peek-art'); if (art) trailer.core.setRect(physicalRect(art)); }
}

function onBoxClick(e) {
    const open = e.target.closest('[data-peek-open]');
    if (open) {
        // La ficha se abre «desde» la carátula: así se recuerdan los títulos de alrededor (pasar de ficha deslizando).
        e.preventDefault();
        e.stopPropagation();
        const card = anchor;
        hidePeek();
        if (card && document.contains(card)) card.click();
        return;
    }
    if (e.target.closest('[data-play], [data-play-episode], [data-play-live]')) setTimeout(hidePeek, 0); // tras el manejador general
}

function refreshFav() {
    if (!box) return;
    const btn = box.querySelector('[data-fav]');
    if (!btn) return;
    const [kind, id] = btn.getAttribute('data-fav').split(':');
    const fav = catalog.isFavorite(kind, id);
    btn.classList.toggle('is-active', fav);
    btn.querySelector('.mi').textContent = fav ? 'check' : 'add';
    btn.setAttribute('aria-label', fav ? t('common.favoriteRemove') : t('common.favoriteAdd'));
}

const enabled = () => settings.get('peekCard') !== false; // ajuste «Tarjeta ampliada»

function onOver(e) {
    if (!enabled() || playerOpen()) { if (box) hidePeek(); return; }
    if (e.target.closest && e.target.closest('.peek')) { clearTimeout(hideTimer); hideTimer = null; return; }
    const card = cardOf(e.target);
    if (card) schedule(card, HOVER_MS);
    else scheduleHide();
}

function onNavFocus(e) {
    if (!enabled() || playerOpen()) { if (box) hidePeek(); return; }
    const card = cardOf(e.target);
    if (card) schedule(card, FOCUS_MS);
    else hidePeek();
}

function onScroll() {
    if (!box) return;
    // En la tele el foco desplaza la fila con suavidad: la tarjeta sigue a la carátula. Con ratón, al desplazar se cierra.
    if (device.isTV && anchor && document.contains(anchor)) place(anchor);
    else hidePeek();
}

/** Sonido de la vista previa desde el mando: la tecla play/pausa (o «m» en un teclado) lo activa o lo silencia. */
function onSoundKey(e) {
    if (!box || !trailer || trailer.button.hidden) return;
    const code = e.keyCode || e.which || 0;
    const media = e.key === 'MediaPlayPause' || e.key === 'MediaPlay' || e.key === 'MediaPause' || code === 179 || code === 415 || code === 19 || code === 10252;
    if (!media && !((e.key === 'm' || e.key === 'M') && !/^(INPUT|TEXTAREA|SELECT)$/.test((e.target && e.target.tagName) || ''))) return;
    e.preventDefault();
    e.stopPropagation();
    setPreviewMuted(!previewMuted());
}

export function initPeek() {
    const app = document.getElementById('app');
    if (!app || device.hasTouch && !device.isTV && !device.hasHover) return;
    if (device.hasHover) {
        app.addEventListener('mouseover', onOver);
        document.documentElement.addEventListener('mouseleave', hidePeek);
    }
    app.addEventListener('nav:focus', onNavFocus);
    // Pulsar la carátula (o el canal) abre la ficha o el reproductor: la tarjeta y su vídeo se cierran en ese momento.
    app.addEventListener('click', (e) => { if (box && cardOf(e.target)) hidePeek(); }, true);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', hidePeek);
    window.addEventListener('keydown', (e) => { if (e.key === 'Escape') hidePeek(); });
    window.addEventListener('keydown', onSoundKey, true);
    ['page:beforeout', 'popup:open', 'dialog:open', 'actions:open', 'popover:open', 'sheet:open'].forEach((ev) => document.addEventListener(ev, hidePeek));
    catalog.on('favorites', refreshFav);
}
